import React from "react";
import { useI18n } from "../../packages/excalidraw/i18n";
import { WelcomeScreen } from "../../packages/excalidraw/index";
import { isExcalidrawPlusSignedUser } from "../app_constants";
import { POINTER_EVENTS } from "../../packages/excalidraw/constants";
import { NotionIcon, file } from "../../packages/excalidraw/components/icons";
import { dispatchCanvasEvent } from "../../packages/excalidraw/events";
import { useAuth } from "../hooks/useAuth";
import { useOpenAgentsDrawer } from "./agents/AgentsSidebar";
import { plugIcon } from "./agents/plugIcon";

export const AppWelcomeScreen: React.FC<{
  onCollabDialogOpen: () => any;
  isCollabEnabled: boolean;
}> = React.memo(() => {
  const { t } = useI18n();
  const { session } = useAuth();
  const isSignedIn = !!session?.access_token;
  const openAgents = useOpenAgentsDrawer();
  let headingContent;
  let anotherHeading;

  if (isExcalidrawPlusSignedUser) {
    headingContent = t("welcomeScreen.app.center_heading_plus")
      .split(/(Excalidraw\+)/)
      .map((bit, idx) => {
        if (bit === "Excalidraw+") {
          return (
            <a
              style={{ pointerEvents: POINTER_EVENTS.inheritFromUI }}
              href={`${
                import.meta.env.VITE_APP_PLUS_APP
              }?utm_source=excalidraw&utm_medium=app&utm_content=welcomeScreenSignedInUser`}
              key={idx}
            >
              Mindmap
            </a>
          );
        }
        return bit;
      });
  } else {
    headingContent = t("welcomeScreen.app.center_heading");
    anotherHeading = t("welcomeScreen.app.another_heading");
  }

  return (
    <WelcomeScreen>
      <WelcomeScreen.Hints.MenuHint>
        {t("welcomeScreen.app.menuHint")}
      </WelcomeScreen.Hints.MenuHint>
      <WelcomeScreen.Hints.ToolbarHint />
      <WelcomeScreen.Hints.HelpHint />
      <WelcomeScreen.Center>
        <WelcomeScreen.Center.Logo />
        <WelcomeScreen.Center.Heading>
          {headingContent} <br />
          {anotherHeading}
        </WelcomeScreen.Center.Heading>
        <WelcomeScreen.Center.Menu>
          {/* Ordered by what a signed-in user actually wants here. "Open" alone
              sent the one click this screen offers into the OS file picker —
              the least likely intent, and one that replaces the scene of the
              cloud file they have open. Both of these are reachable from the
              hamburger menu already; this is about which is offered FIRST. */}
          {isSignedIn && (
            <>
              <WelcomeScreen.Center.MenuItem
                data-testid="welcome-import-notion"
                shortcut="N"
                icon={NotionIcon}
                // The same event the toolbar's Notion button fires. Selecting
                // the tool instead only changes the cursor: the picker is a
                // dialog, opened by this event, not by the active tool.
                onSelect={() =>
                  dispatchCanvasEvent("toolbarOptionSelected", {
                    type: "notion",
                  })
                }
              >
                {t("toolBar.notion")}
              </WelcomeScreen.Center.MenuItem>
              <WelcomeScreen.Center.MenuItem
                data-testid="welcome-my-mindmaps"
                // No shortcut shown: Cmd+O stays bound to the file picker
                // below, and advertising it here would open the wrong thing.
                shortcut={null}
                icon={file}
                onSelect={() => {
                  window.location.href = "/files";
                }}
              >
                {t("welcomeScreen.app.myMindmaps")}
              </WelcomeScreen.Center.MenuItem>
            </>
          )}
          {/* "Open a file" rather than "Open": bare "Open" reads as "open my
              stuff", which is exactly what it does not do. */}
          <WelcomeScreen.Center.MenuItemLoadScene />
          <WelcomeScreen.Center.MenuItem
            data-testid="welcome-connect-agent"
            shortcut={null}
            icon={plugIcon}
            onSelect={openAgents}
          >
            Connect an AI agent
          </WelcomeScreen.Center.MenuItem>
        </WelcomeScreen.Center.Menu>
      </WelcomeScreen.Center>
    </WelcomeScreen>
  );
});
